import React, { useState, useEffect } from 'react';
import FormSection from './FormSection';
import { Input } from '../common';
import Select from '../common/Select';
import MultiSelect from '../common/MultiSelect';
import { supabase } from '../../supabaseClient';
import { 
  LOGISTIC_REQUIREMENT_OPTIONS
} from './constants';

const ScheduleLocation = ({ formData, onChange, errors = {} }) => {
  const [stationOptions, setStationOptions] = useState([]);

  // Fetch station options from database
  useEffect(() => {
    const fetchStations = async () => {
      try {
        const { data, error } = await supabase
          .from('line_station_information')
          .select('iata_code, name_of_airport')
          .order('iata_code');

        if (error) throw error;

        const options = data.map(station => ({
          value: station.iata_code,
          label: `${station.iata_code} - ${station.name_of_airport}`
        }));

        setStationOptions(options);
      } catch (error) {
        console.error('Error fetching stations:', error);
      }
    };

    fetchStations();
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    // Clear error for the field if it has a value
    const updatedData = { [name]: value };
    const updatedErrors = { ...errors };
    
    if (value) {
      delete updatedErrors[name];
      
      // If station is selected, fetch station details
      if (name === 'station') {
        try {
          const { data: stationData, error } = await supabase
            .from('line_station_information')
            .select('*')
            .eq('iata_code', value)
            .single();

          if (error) throw error;

          if (stationData) {
            updatedData.stationDetails = {
              iataCode: stationData.iata_code,
              cityName: stationData.city_name,
              country: stationData.country,
              gmt: stationData.gmt,
              operatingPattern: stationData.crew_operating_pattern
            };
          } else {
            // Clear station details if no data found
            updatedData.stationDetails = null;
          }
        } catch (error) {
          console.error('Error fetching station details:', error);
          // Clear station details on error
          updatedData.stationDetails = null;
        }
      }
      
      onChange(updatedData, updatedErrors);
    } else {
      // If clearing station, also clear station details
      if (name === 'station') {
        updatedData.stationDetails = null;
      }
      onChange(updatedData);
    }
  };

  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for the field if it has values
    const updatedData = { [name]: value };
    const updatedErrors = { ...errors };
    
    if (value && value.length > 0) {
      delete updatedErrors[name];
      onChange(updatedData, updatedErrors);
    } else {
      onChange(updatedData);
    }
  };

  // Calculate end date based on scheduled date and duration
  const calculateEndDate = (scheduledDate, duration) => {
    if (!scheduledDate || !duration) return '';
    const date = new Date(scheduledDate);
    date.setDate(date.getDate() + parseInt(duration));
    return date.toISOString().split('T')[0];
  };

  return (
    <FormSection title="Schedule & Location">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Input
            label="Scheduled Date"
            name="scheduledDate"
            type="date"
            value={formData.scheduledDate}
            onChange={handleInputChange}
            error={errors.scheduledDate}
            required
          />

          <Input
            label="Duration (Days)"
            name="duration"
            type="number"
            min="1"
            value={formData.duration}
            onChange={handleInputChange}
            error={errors.duration}
            required
          />

          <Input
            label="Calculated End Date"
            type="date"
            value={calculateEndDate(formData.scheduledDate, formData.duration)}
            disabled
            className="bg-gray-50"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Select
              label="Station"
              name="station"
              value={formData.station}
              onChange={handleInputChange}
              options={stationOptions}
              error={errors.station}
              placeholder="Select a station"
              required
            />
            {formData.stationDetails && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="font-medium">IATA Code:</span> {formData.stationDetails.iataCode}
                  </div>
                  <div>
                    <span className="font-medium">City:</span> {formData.stationDetails.cityName}
                  </div>
                  <div>
                    <span className="font-medium">Country:</span> {formData.stationDetails.country}
                  </div>
                  <div>
                    <span className="font-medium">GMT:</span> {formData.stationDetails.gmt}
                  </div>
                  <div>
                    <span className="font-medium">Operating Pattern:</span> {formData.stationDetails.operatingPattern}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Input
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            error={errors.location}
            placeholder="Enter address, name of facility, or area"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MultiSelect
            label="Logistic Requirements"
            name="logisticRequirement"
            value={formData.logisticRequirement || []}
            onChange={handleMultiSelectChange}
            options={LOGISTIC_REQUIREMENT_OPTIONS}
            error={errors.logisticRequirement}
          />

          <Input
            label="Confirmed Audit Date"
            name="confirmedAuditDate"
            type="date"
            value={formData.confirmedAuditDate}
            onChange={handleInputChange}
            error={errors.confirmedAuditDate}
          />
        </div>
      </div>
    </FormSection>
  );
};

export default ScheduleLocation;
